import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconChevronRight, IconDelete, IconExpense, IconFolderOpen } from '../../icons';
import DataTable, { Column } from '../common/DataTable';
import RowActionsMenu, { RowActionsMenuItem } from '../common/RowActionsMenu';
import ChannelBadge from './ChannelBadge';
import UnifiedStatusBadge from './UnifiedStatusBadge';
import InboundQualityChip from './InboundQualityChip';
import { formatUnifiedAmount, formatUnifiedDate } from './unifiedInboxFormat';
import { resolveRowActions, RowActionContext } from './inboxRowActions';
import type { UnifiedInboxItem } from '../../utils/unifiedInbox/types';

/**
 * `inbox/UnifiedInboxTable` — the ONE list of the Unified Inbox (§6.5): a
 * `DataTable` (M-UX-2.3) over the already merged+sorted+filtered
 * {@link UnifiedInboxItem} array, with the Kanal-Badge/Status-Badge/
 * Qualitäts-Badges/Aktionen columns the architecture spec calls for.
 *
 * Purely presentational — sorting/filtering/loading state live in the parent
 * (`UnifiedInboxPage`); this component only renders rows and reports clicks
 * via callbacks, so it stays trivially unit-testable with a plain item array.
 *
 * @module components/inbox/UnifiedInboxTable
 */
export interface UnifiedInboxTableProps {
  items: UnifiedInboxItem[];
  /** §6.5 Buchen-Gate: `EXPENSES_ENABLED && isPremium && !isReadOnly`. */
  canBookExpense: boolean;
  /** `useIsReadOnly()` — additionally gates "Öffnen" for scan rows (§6.5 Advisor read-only). */
  isReadOnly: boolean;
  /** "Öffnen" — dispatches to the per-source detail component (parent decides which). */
  onOpen: (item: UnifiedInboxItem) => void;
  /** "Als Ausgabe verbuchen" — opens the per-source booking modal (parent decides which). */
  onBook: (item: UnifiedInboxItem) => void;
  /** "Verwerfen" (Soft-Discard, M-V4-D2) — opens the parent's confirmation flow (§4.3). */
  onDiscard: (item: UnifiedInboxItem) => void;
}

export const UnifiedInboxTable: React.FC<UnifiedInboxTableProps> = ({
  items,
  canBookExpense,
  isReadOnly,
  onOpen,
  onBook,
  onDiscard,
}) => {
  const { t } = useTranslation('posteingang');

  const rowActionCtx: RowActionContext = useMemo(
    () => ({ canBookExpense, isReadOnly }),
    [canBookExpense, isReadOnly],
  );

  const columns: Column<UnifiedInboxItem>[] = [
    {
      id: 'channel',
      header: t('unifiedInbox.table.channel', 'Kanal'),
      accessor: (row) => <ChannelBadge channel={row.channel} />,
      sortValue: (row) => row.channel,
    },
    {
      id: 'supplier',
      header: t('unifiedInbox.table.supplier', 'Lieferant'),
      accessor: (row) => row.supplierName || t('unknownSender', 'Unbekannter Absender'),
      sortValue: (row) => row.supplierName ?? '',
      // §6.5 (a): Slack aus der schmaleren qualityBadges-Spalte wandert hierher.
      truncate: 'max-w-none lg:max-w-[22rem]',
    },
    {
      id: 'documentNo',
      header: t('unifiedInbox.table.documentNo', 'Beleg-Nr.'),
      accessor: (row) => row.documentNo || '—',
      sortValue: (row) => row.documentNo ?? '',
      hideBelow: 'xl',
    },
    {
      id: 'date',
      header: t('unifiedInbox.table.date', 'Datum'),
      accessor: (row) => formatUnifiedDate(row.receivedAtMs),
      sortValue: (row) => row.receivedAtMs,
    },
    {
      id: 'amount',
      header: t('unifiedInbox.table.amount', 'Betrag'),
      accessor: (row) => formatUnifiedAmount(row.grossCents, row.currency),
      sortValue: (row) => row.grossCents ?? -Infinity,
      align: 'right',
      numeric: true,
    },
    {
      id: 'status',
      header: t('unifiedInbox.table.status', 'Status'),
      // 🔴 stopPropagation (plan-ux-status-chips-storno-inbox.md §B): der
      // StatusChip-Trigger ist ein `<button>` — ohne diesen Wrapper würde ein
      // Klick zum Pinnen des Popovers `onRowClick` (Zeilen-Navigation)
      // mitauslösen. Gleiches Muster wie die Aktions-Zelle unten.
      accessor: (row) => (
        <span onClick={(e) => e.stopPropagation()}>
          <UnifiedStatusBadge status={row.status} />
        </span>
      ),
      sortValue: (row) => row.status,
    },
    {
      id: 'qualityBadges',
      header: t('unifiedInbox.table.quality', 'Prüfung'),
      // M-C: ONE aggregated chip (dominant badge + "+N") instead of the
      // multi-pill `InboundBadgeList` — the full check set is still available
      // via the chip's popover. §6.5 (a): width-capped (`w-32`) — the
      // auto-table-layout otherwise let this column's slack starve the
      // `supplier` column; the chip itself is NOT truncated (portal popover
      // trigger). Hidden below `lg` to keep the laptop viewport free of the
      // inner scrollbox.
      accessor: (row) => <InboundQualityChip badges={row.qualityBadges} />,
      align: 'left',
      className: 'w-32',
      hideBelow: 'lg',
    },
    {
      id: 'actions',
      header: t('unifiedInbox.table.actions', 'Aktionen'),
      align: 'right',
      sticky: 'right',
      accessor: (row) => {
        const actions = resolveRowActions(row, rowActionCtx);

        // Design-v5 §6.5 (b): "Verbuchen" moves INTO the ⋯-RowActionsMenu as
        // the first, accent-emphasised item — Inbox now matches Expenses/
        // OpenItems/Archive (the only tables that still had a labeled visible
        // button). The Aktionen-column shrinks to the bare ⋯-trigger.
        const menuItems: RowActionsMenuItem[] = [];
        if (actions.canBook) {
          menuItems.push({
            id: 'book',
            label: t('unifiedInbox.actions.book', 'Verbuchen'),
            icon: <IconExpense />,
            emphasis: 'accent',
            onSelect: () => onBook(row),
          });
        }
        if (actions.canOpen) {
          menuItems.push({
            id: 'open',
            label: t('unifiedInbox.actions.open', 'Öffnen'),
            icon: <IconFolderOpen />,
            onSelect: () => onOpen(row),
          });
        }
        if (actions.canViewExpense) {
          menuItems.push({
            id: 'viewExpense',
            label: t('unifiedInbox.actions.viewExpense', 'Ausgabe ansehen'),
            icon: <IconChevronRight />,
            to: '/dashboard/ausgaben',
          });
        }
        if (actions.canDiscard) {
          menuItems.push({
            id: 'discard',
            label: t('unifiedInbox.actions.discard', 'Verwerfen'),
            icon: <IconDelete />,
            variant: 'danger',
            onSelect: () => onDiscard(row),
          });
        }

        return (
          <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
            <RowActionsMenu
              items={menuItems}
              triggerLabel={t('unifiedInbox.table.actions', 'Aktionen')}
              data-testid={`unified-inbox-row-actions-${row.key}`}
            />
          </div>
        );
      },
    },
  ];

  return (
    <DataTable<UnifiedInboxItem>
      columns={columns}
      rows={items}
      rowKey={(row) => row.key}
      density="compact"
      onRowClick={(row) => {
        const actions = resolveRowActions(row, rowActionCtx);
        if (actions.canOpen) onOpen(row);
      }}
      initialSort={{ id: 'date', dir: 'desc' }}
      empty={
        <div>
          <p className="text-secondary font-medium">{t('unifiedInbox.empty.title', 'Noch keine Eingänge')}</p>
          <p className="text-muted text-sm mt-1">
            {t(
              'unifiedInbox.empty.body',
              'Laden Sie oben einen Beleg hoch, richten Sie den E-Mail-Empfang ein oder fotografieren Sie einen Beleg.',
            )}
          </p>
        </div>
      }
    />
  );
};

export default UnifiedInboxTable;
